import { useEffect, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scheduleRefresh, watchLayoutDependencies } from '@/hooks/scrollRefresh';

gsap.registerPlugin(ScrollTrigger);

const EASE = 'power4.out';

const clipFrom: Record<string, string> = {
  clip: 'inset(100% 0% 0% 0%)',
  'clip-left': 'inset(0% 100% 0% 0%)',
  'clip-right': 'inset(0% 0% 0% 100%)',
  'clip-center': 'inset(18% 18% 18% 18% round 24px)',
};

/**
 * Shared secondary motion. Content stays visible without JS / with reduced motion.
 * [data-reveal="lines"] line masks rise
 * [data-reveal="up"] fade + rise
 * [data-reveal="clip|clip-left|clip-right|clip-center"] directional clip + image scale restore
 * [data-scrub="zoom"] image scale restores with scroll position (scrubbed)
 */
export default function useScrollMotion(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!root.current) return undefined;
    const mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const ctx = gsap.context(() => {
        gsap.utils.toArray<HTMLElement>('[data-reveal="lines"]').forEach((el) => {
          gsap.from(el.querySelectorAll('.line-inner'), {
            yPercent: 110,
            duration: 1.1,
            ease: EASE,
            stagger: 0.08,
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none reverse' },
          });
        });
        gsap.utils.toArray<HTMLElement>('[data-reveal="up"]').forEach((el) => {
          gsap.from(el, {
            y: 32,
            opacity: 0,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none reverse' },
          });
        });
        Object.keys(clipFrom).forEach((kind) => {
          gsap.utils.toArray<HTMLElement>(`[data-reveal="${kind}"]`).forEach((el) => {
            const img = el.querySelector('img');
            const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 82%', toggleActions: 'play none none reverse' } });
            tl.fromTo(el, { clipPath: clipFrom[kind] }, { clipPath: 'inset(0% 0% 0% 0% round 0px)', duration: 1.3, ease: 'power4.inOut', clearProps: 'clipPath' });
            if (img) tl.from(img, { scale: 1.22, duration: 1.7, ease: 'power3.out' }, 0);
          });
        });
        gsap.utils.toArray<HTMLElement>('[data-scrub="zoom"]').forEach((el) => {
          const img = el.querySelector('img');
          if (!img) return;
          gsap.fromTo(img, { scale: 1.18 }, {
            scale: 1, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'center center', scrub: true },
          });
        });
      }, root);
      return () => ctx.revert();
    });

    // All component triggers (pins included) exist by now: sort by priority/position, then measure.
    scheduleRefresh();
    const stopWatching = watchLayoutDependencies(document);
    // Breakpoint flips (matchMedia revert/re-add) change pin spacing: re-measure everything.
    const bp = window.matchMedia('(min-width: 1024px)');
    bp.addEventListener('change', scheduleRefresh);

    return () => {
      stopWatching();
      bp.removeEventListener('change', scheduleRefresh);
      mm.revert();
    };
  }, [root]);
}